import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

import type { OptimizationBlockState } from '../../../lib/optimization/types'

import OptimizationBlockBadge from './OptimizationBlockBadge'

interface OptimizationBlockOverlayProps {
  block?: OptimizationBlockState
  children: ReactNode
  onFailureClick?: (block: OptimizationBlockState) => void
  className?: string
  /** When false, keep highlight rings only (no duration badge). */
  showBadge?: boolean
  /**
   * When false, skip the running scrim (editor: header badge + focus ring are enough).
   */
  showRunningScrim?: boolean
  /** When false, hide the emerald ring on done/skipped blocks (post-run review uses amber only). */
  showCompleteRing?: boolean
}

const badgeAnchorClass =
  'absolute right-2 top-3 z-30 flex items-center'

export default function OptimizationBlockOverlay({
  block,
  children,
  onFailureClick,
  className = '',
  showBadge = true,
  showRunningScrim = true,
  showCompleteRing = true,
}: OptimizationBlockOverlayProps) {
  const active = block && block.status !== 'pending'

  const completeLike = block?.status === 'done' || block?.status === 'skipped'

  return (
    <div className={`relative ${className}`} data-opt-block={block?.block_id}>
      {children}
      {active && block.status === 'running' && showRunningScrim && (
        <div
          className="pointer-events-none absolute inset-0 z-20 rounded-lg border-2 border-interactive/70 ring-2 ring-interactive/20"
          aria-hidden
          data-testid="optimization-block-running-ring"
        >
          {showBadge && (
            <div
              className={`${badgeAnchorClass} gap-1 rounded-md bg-surface-default/90 px-2 py-1 text-xs font-medium text-brand-primary shadow-sm`}
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Optimizing…
            </div>
          )}
        </div>
      )}
      {active && completeLike && showCompleteRing && (
        <div
          className="pointer-events-none absolute inset-0 z-10 rounded-lg ring-1 ring-emerald-300/60"
          aria-hidden
          data-testid="optimization-block-complete-ring"
        />
      )}
      {active && showBadge && block.status === 'failed' && (
        <div className={badgeAnchorClass}>
          <OptimizationBlockBadge block={block} onFailureClick={onFailureClick} />
        </div>
      )}
      {active && showBadge && block.status !== 'failed' && (
        <div className={`${badgeAnchorClass} pointer-events-none`}>
          <OptimizationBlockBadge block={block} />
        </div>
      )}
      {active && block.status === 'failed' && (
        <div
          className="pointer-events-none absolute inset-0 z-20 rounded-lg border-2 border-amber-400/60 bg-amber-500/5"
          aria-hidden
        />
      )}
    </div>
  )
}
